import {
	getFormProps,
	getInputProps,
	getSelectProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import { type ActionFunctionArgs } from '@remix-run/node'
import { Form, json, useParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	CheckboxField,
	Field,
	SelectField,
	TextareaField,
} from '#app/components/forms.js'
import { Separator } from '#app/components/ui/separator.js'
import { StatusButton } from '#app/components/ui/status-button.js'
import { prisma } from '#app/utils/db.server.js'
import { sendEmail } from '#app/utils/email.server.js'
import { checkHoneypot } from '#app/utils/honeypot.server.js'
import { useIsPending } from '#app/utils/misc.js'
import { redirectWithToast } from '#app/utils/toast.server.js'
import { EmailSchema } from '#app/utils/user-validation.js'

const MembershipApplicationSchema = z.object({
	membershipType: z.enum(['beneficiary', 'patron']),
	name: z.string().trim(),
	email: EmailSchema,
	applicationType: z.enum(['individual', 'organisation']),
	organisationName: z.string().trim().optional(),
	typeOfWork: z.enum([
		'non-profit',
		'for-profit',
		'scientist',
		'activist',
		'educator',
		'student',
		'artist',
		'other',
	]),
	projectDescription: z.string().trim(),
	location: z.string().trim(),
	numberOfPeople: z.enum(['one-to-ten', 'eleven-to-thirty', 'over-thirty']),
	numberOfSalariedPeople: z.enum([
		'zero',
		'one-to-ten',
		'eleven-to-thirty',
		'over-thirty',
	]),
	links: z.string().trim(),
	registeredNonProfit: z.boolean().default(false),
	mediaUseCase: z.enum([
		'fundraising',
		'social-media',
		'website',
		'impact-campaign',
		'education',
		'art',
		'policy',
		'other',
	]),
	otherUseCase: z.string().trim().min(1).optional(),
	howDidYouHearAboutUs: z.string().trim(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: MembershipApplicationSchema,
		async: true,
	})
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	await prisma.membershipApplication.create({
		data: {
			...submission.value,
		},
	})
	await sendEmail({
		to: submission.value.email,
		subject: `Kogia | Membership Application Received`,
		react: <MembershipApplicationEmail name={submission.value.name} />,
	})
	return redirectWithToast('/', {
		title: 'Thank you!',
		description: 'We have received your application. We will be in touch',
	})
}

export function MembershipApplicationEmail({ name }: { name: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Thank you for your application!</E.Preview>
			<E.Body>
				<E.Container>
					<E.Heading as="h1">Thank you for your application!</E.Heading>
					<E.Text>Dear {name},</E.Text>
					<E.Text>
						Thank you very much for applying to be a Kogia library member!
					</E.Text>
					<E.Text>
						We will soon be reviewing your application, and we will get back to
						you as soon as possible. Once again, thanks again for your
						commitment to our shared mission.
					</E.Text>
					<E.Text>
						If you have any questions or wish to provide more details, please
						don't hesitate to reach us at{' '}
						<E.Link href={'mailto:info@kogia.org'}>info@kogia.org</E.Link>.
					</E.Text>
					<E.Text>All the best,</E.Text>
					<E.Text>The Kogia Team</E.Text>
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export default function MembershipApply() {
	const params = useParams()
	const isPending = useIsPending({
		formAction: `/${params.membership}/apply`,
		formMethod: 'POST',
	})
	const [form, fields] = useForm({
		id: params.membership + '-membership-apply',
		constraint: getZodConstraint(MembershipApplicationSchema),
		// lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MembershipApplicationSchema })
		},
		shouldRevalidate: 'onBlur',
		shouldValidate: 'onInput',
	})
	return (
		<div className="flex w-full max-w-screen-md flex-col gap-3 p-4 md:p-8">
			<div className="flex items-center gap-4">
				<h2 className="font-serif text-h2">
					Request {params.membership === 'patron' ? 'Patron' : 'Beneficiary'}{' '}
					Access
				</h2>
				<Separator className="flex-1" />
			</div>
			<Form
				action={`/${params.membership}/apply`}
				method="POST"
				{...getFormProps(form)}
				className="w-full"
			>
				<HoneypotInputs />
				<input
					type="text"
					hidden
					readOnly
					name="membershipType"
					value={
						params?.membership === 'beneficiary' ? 'beneficiary' : 'patron'
					}
				/>
				<Field
					className="flex-1"
					labelProps={{
						htmlFor: fields.name.id,
						children: 'Name',
					}}
					inputProps={{
						...getInputProps(fields.name, { type: 'text' }),
						name: 'name',
					}}
					errors={fields.name.errors}
				/>
				<Field
					labelProps={{
						htmlFor: fields.email.id,
						children: 'Email',
					}}
					inputProps={{
						...getInputProps(fields.email, { type: 'email' }),
						name: 'email',
					}}
					errors={fields.email.errors}
				/>
				<SelectField
					labelProps={{
						htmlFor: fields.applicationType.id,
						children: 'Application Type',
					}}
					selectProps={{ ...getSelectProps(fields.applicationType) }}
					mySelectProps={{
						name: 'applicationType',
						isMulti: false,
						isSearchable: false,
						defaultOptions: [],
						options: [
							{
								label: 'Individual',
								value: 'individual',
							},
							{
								label: 'Organisation',
								value: 'organisation',
							},
						],
						singularItem: 'type of application',
						onChange: () => form.validate({ name: 'applicationType' }),
						placeholder: 'Select Application Type',
					}}
					errors={fields.applicationType.errors}
				/>
				<Field
					labelProps={{
						htmlFor: fields.organisationName.id,
						children: 'Organisation Name',
					}}
					inputProps={{
						...getInputProps(fields.organisationName, { type: 'text' }),
						name: 'organisationName',
					}}
					errors={fields.organisationName.errors}
				/>
				<SelectField
					labelProps={{
						htmlFor: fields.typeOfWork.id,
						children: 'Work Type',
					}}
					selectProps={{ ...getSelectProps(fields.typeOfWork) }}
					mySelectProps={{
						name: 'typeOfWork',
						isMulti: false,
						isSearchable: false,
						defaultOptions: [],
						options: [
							{ value: 'non-profit', label: 'Non-profit' },
							{ value: 'for-profit', label: 'For Profit' },
							{ value: 'scientist', label: 'Scientist' },
							{ value: 'activist', label: 'Activist' },
							{ value: 'educator', label: 'Educator' },
							{ value: 'student', label: 'Student' },
							{ value: 'artist', label: 'Artist' },
							{ value: 'other', label: 'Other' },
						],
						singularItem: 'type of work',
						onChange: () => form.validate({ name: 'typeOfWork' }),
						placeholder: 'Select Work Type',
					}}
					errors={fields.typeOfWork.errors}
				/>
				<TextareaField
					labelProps={{
						htmlFor: fields.projectDescription.id,
						children: 'Project Description',
					}}
					textareaProps={{
						...getTextareaProps(fields.projectDescription),
						name: 'projectDescription',
					}}
					errors={fields.projectDescription.errors}
				/>
				<Field
					labelProps={{
						htmlFor: fields.location.id,
						children: 'Primary Work Location',
					}}
					inputProps={{
						...getInputProps(fields.location, { type: 'text' }),
						name: 'location',
					}}
					errors={fields.location.errors}
				/>
				<SelectField
					labelProps={{
						htmlFor: fields.numberOfPeople.id,
						children: 'Number of people',
					}}
					selectProps={{ ...getSelectProps(fields.numberOfPeople) }}
					mySelectProps={{
						name: 'numberOfPeople',
						isMulti: false,
						isSearchable: false,
						defaultOptions: [],
						options: [
							{ value: 'one-to-ten', label: '1 - 10' },
							{ value: 'eleven-to-thirty', label: '11 - 30' },
							{ value: 'over-thirty', label: '30+' },
						],
						singularItem: 'person',
						pluralItem: 'people',
						onChange: () => form.validate({ name: 'numberOfPeople' }),
						placeholder: 'Select number of people',
					}}
					errors={fields.numberOfPeople.errors}
				/>
				<SelectField
					labelProps={{
						htmlFor: fields.numberOfSalariedPeople.id,
						children: 'Number of salaried people',
					}}
					selectProps={{ ...getSelectProps(fields.numberOfSalariedPeople) }}
					mySelectProps={{
						name: 'numberOfSalariedPeople',
						isMulti: false,
						isSearchable: false,
						defaultOptions: [],
						options: [
							{ value: 'zero', label: '0' },
							{ value: 'one-to-ten', label: '1 - 10' },
							{ value: 'eleven-to-thirty', label: '11 - 30' },
							{ value: 'over-thirty', label: '30+' },
						],
						singularItem: 'person',
						pluralItem: 'people',
						onChange: () => form.validate({ name: 'numberOfSalariedPeople' }),
						placeholder: 'Select number of salaried people',
					}}
					errors={fields.numberOfSalariedPeople.errors}
				/>
				<CheckboxField
					labelProps={{
						htmlFor: fields.registeredNonProfit.id,
						children: 'Are you a registered non-profit?',
					}}
					buttonProps={getInputProps(fields.registeredNonProfit, {
						type: 'checkbox',
					})}
					errors={fields.registeredNonProfit.errors}
				/>
				<SelectField
					labelProps={{
						htmlFor: fields.mediaUseCase.id,
						children: 'Media Use Case',
					}}
					selectProps={{ ...getSelectProps(fields.mediaUseCase) }}
					mySelectProps={{
						name: 'mediaUseCase',
						isMulti: false,
						isSearchable: false,
						defaultOptions: [],
						options: [
							{ value: 'fundraising', label: 'Fundraising' },
							{ value: 'social-media', label: 'Social Media' },
							{ value: 'website', label: 'Website' },
							{ value: 'impact-campaign', label: 'Impact Campaign' },
							{ value: 'education', label: 'Education' },
							{ value: 'art', label: 'Art' },
							{ value: 'policy', label: 'Policy' },
							{ value: 'other', label: 'Other' },
						],
						singularItem: 'person',
						pluralItem: 'people',
						onChange: () => form.validate({ name: 'mediaUseCase' }),
						placeholder: 'Select media use case',
					}}
					errors={fields.mediaUseCase.errors}
				/>
				{fields.mediaUseCase.value === 'other' && (
					<Field
						labelProps={{
							htmlFor: fields.otherUseCase.id,
							children: 'Other reason',
						}}
						inputProps={{
							...getInputProps(fields.otherUseCase, { type: 'text' }),
							name: 'otherUseCase',
						}}
						errors={fields.otherUseCase.errors}
					/>
				)}
				<TextareaField
					labelProps={{
						htmlFor: fields.links.id,
						children: 'Links',
					}}
					textareaProps={{
						...getTextareaProps(fields.links),
						name: 'links',
					}}
					errors={fields.links.errors}
				/>
				<TextareaField
					labelProps={{
						htmlFor: fields.howDidYouHearAboutUs.id,
						children: 'How did you hear about us?',
					}}
					textareaProps={{
						...getTextareaProps(fields.howDidYouHearAboutUs),
						name: 'howDidYouHearAboutUs',
					}}
					errors={fields.howDidYouHearAboutUs.errors}
				/>
				<div className="mt-4 flex items-center justify-between gap-6">
					<StatusButton
						className="mt-4 w-full"
						status={isPending ? 'pending' : form.status ?? 'idle'}
						type="submit"
						disabled={isPending}
					>
						Apply
					</StatusButton>
				</div>
			</Form>
		</div>
	)
}
